<template>
    <!-- Flowbite-style bordered alert (https://flowbite.com/docs/components/alerts/)
         translated to the app's standard Tailwind palette, using inline Tabler icons
         (https://tabler.io/icons) like the rest of the app. Single reusable component:
         pick a color, optionally a title, an icon, and a dismiss button. -->
    <div v-if="open"
         :class="plain
             ? colorClasses.plainText
             : [colorClasses.container, bordered ? colorClasses.border : '', 'p-4 rounded-lg']"
         class="text-sm"
         role="alert">
        <div class="flex" :class="[headerAlign, { 'justify-between': dismissible && !title }]">
            <div class="flex" :class="headerAlign">
                <!-- Icon: Tabler SVG. Defaults per color; override with :icon="'<svg .../>'" or hide with :icon="false". -->
                <span v-if="iconSvg"
                      v-html="iconSvg"
                      :class="colorClasses.iconColor"
                      class="shrink-0 me-2"></span>

                <div>
                    <h3 v-if="title" class="text-sm font-medium leading-5 m-0 p-0" :class="colorClasses.title">{{ title }}</h3>
                    <!-- Inline (no title): bold lead + message on one line, like Flowbite's default alert. -->
                    <p v-else-if="lead" class="mb-0"><span class="font-semibold me-1">{{ lead }}</span><slot></slot></p>
                    <div v-else :class="colorClasses.body"><slot></slot></div>
                </div>
            </div>

            <button v-if="dismissible"
                    type="button"
                    aria-label="Close"
                    @click="dismiss"
                    v-html="closeIcon"
                    :class="colorClasses.dismiss"
                    class="ms-auto -mx-1.5 -my-1.5 rounded focus:outline-none focus:ring-2 inline-flex items-center justify-center h-8 w-8 shrink-0"></button>
        </div>

        <!-- Title + description layout: description sits below the title row. -->
        <div v-if="title && hasContent" class="mt-2" :class="colorClasses.body">
            <slot></slot>
        </div>
    </div>
</template>

<script>
// Tabler outline icons (24x24, stroke=currentColor). Copied inline per the app convention
// (see AlarmStatusBadge.vue) so no icon package dependency is needed.
const TABLER = {
    'alert-triangle': '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 9v4" /><path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" /><path d="M12 16h.01" /></svg>',
    'alert-circle': '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" /><path d="M12 8v4" /><path d="M12 16h.01" /></svg>',
    'info-circle': '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" /><path d="M12 9h.01" /><path d="M11 12h1v4h1" /></svg>',
    'circle-check': '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /><path d="M9 12l2 2l4 -4" /></svg>',
    'x': '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M18 6l-12 12" /><path d="M6 6l12 12" /></svg>',
};

export default {
    name: "Alert",
    props: {
        // info | success | warning | danger | dark
        color: {
            type: String,
            default: 'info',
        },
        // Optional bold heading rendered above the description.
        title: {
            type: String,
            default: '',
        },
        // Optional bold lead phrase for the inline (title-less) layout.
        lead: {
            type: String,
            default: '',
        },
        // Draw the border variant (Flowbite "bordered" alert). Default true.
        bordered: {
            type: Boolean,
            default: true,
        },
        // Plain inline callout: icon + colored text only, no background/border/padding box.
        // Use when placing the alert inline next to other controls (e.g. beside a button).
        plain: {
            type: Boolean,
            default: false,
        },
        // Show a dismiss (x) button and hide the alert when clicked.
        dismissible: {
            type: Boolean,
            default: false,
        },
        // Override the default per-color icon: pass a Tabler key ('alert-triangle',
        // 'info-circle', 'circle-check', 'alert-circle') or a raw SVG string. Pass false to hide.
        icon: {
            type: [String, Boolean],
            default: '',
        },
    },
    data() {
        return {
            open: true,
        };
    },
    computed: {
        // Whether the default content slot has anything in it.
        hasContent() {
            return !!this.$slots.default;
        },
        // Icon aligns center with a title (single-line header) or lead; starts at top for a
        // multi-line title-less body so the icon sits by the first line.
        headerAlign() {
            return (this.title || this.lead) ? 'items-center' : 'items-start';
        },
        closeIcon() {
            return TABLER.x;
        },
        iconSvg() {
            if (this.icon === false) {
                return '';
            }
            // Raw SVG override.
            if (typeof this.icon === 'string' && this.icon.trim().startsWith('<svg')) {
                return this.icon;
            }
            // Named Tabler key override.
            if (typeof this.icon === 'string' && this.icon.length && TABLER[this.icon]) {
                return TABLER[this.icon];
            }
            // Per-color default.
            return TABLER[this.colorClasses.icon];
        },
        colorClasses() {
            const palette = {
                info: {
                    container: 'bg-blue-50 text-blue-700',
                    border: 'border border-blue-300',
                    title: 'text-blue-800',
                    body: 'text-blue-700',
                    icon: 'info-circle',
                    iconColor: 'text-blue-500',
                    dismiss: 'text-blue-500 hover:bg-blue-100 focus:ring-blue-400',
                    plainText: 'text-blue-700',
                },
                success: {
                    container: 'bg-green-50 text-green-700',
                    border: 'border border-green-300',
                    title: 'text-green-800',
                    body: 'text-green-700',
                    icon: 'circle-check',
                    iconColor: 'text-green-500',
                    dismiss: 'text-green-500 hover:bg-green-100 focus:ring-green-400',
                    plainText: 'text-green-700',
                },
                warning: {
                    container: 'bg-yellow-50 text-yellow-700',
                    border: 'border border-yellow-300',
                    title: 'text-yellow-800',
                    body: 'text-yellow-700',
                    icon: 'alert-triangle',
                    iconColor: 'text-yellow-500',
                    dismiss: 'text-yellow-500 hover:bg-yellow-100 focus:ring-yellow-400',
                    plainText: 'text-yellow-700',
                },
                danger: {
                    container: 'bg-red-50 text-red-700',
                    border: 'border border-red-300',
                    title: 'text-red-800',
                    body: 'text-red-700',
                    icon: 'alert-circle',
                    iconColor: 'text-red-500',
                    dismiss: 'text-red-500 hover:bg-red-100 focus:ring-red-400',
                    plainText: 'text-red-700',
                },
                dark: {
                    container: 'bg-gray-50 text-gray-700',
                    border: 'border border-gray-300',
                    title: 'text-gray-800',
                    body: 'text-gray-700',
                    icon: 'info-circle',
                    iconColor: 'text-gray-500',
                    dismiss: 'text-gray-500 hover:bg-gray-200 focus:ring-gray-400',
                    plainText: 'text-gray-700',
                },
            };

            return palette[this.color] || palette.info;
        },
    },
    methods: {
        dismiss() {
            this.open = false;
            this.$emit('dismissed');
        },
    },
};
</script>
